import { cn } from "@/lib/utils";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { getLocalizedUrl, isActiveUrl } from "@/lib/localized-routes";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/hooks/use-auth";
import { usePermissions } from "@/hooks/use-permissions";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { useSystemSettings } from "@/hooks/use-system-settings";
import { useTranslation } from 'react-i18next';
import { 
  SidebarHeader, 
  SidebarContent, 
  SidebarGroup, 
  SidebarGroupContent, 
  SidebarGroupLabel, 
  SidebarMenu, 
  SidebarMenuButton, 
  SidebarMenuItem,
  SidebarFooter,
  useSidebar 
} from "@/components/ui/sidebar";
import {
  LayoutDashboard,
  Users,
  CheckSquare,
  Settings,
  Shield,
  FileText,
  Menu,
  X,
  Star,
  CreditCard,
  Calculator,
  PieChart,
  FileCheck,
  UserCog,
  Bell
} from "lucide-react";
import { useState } from "react";
import { createPortal } from "react-dom";
import { type Task } from "@shared/schema";

interface SidebarNavProps extends React.HTMLAttributes<HTMLDivElement> {
  isMobile?: boolean;
}

export function SidebarNav({ className, isMobile = false, ...props }: SidebarNavProps) {
  const [location] = useLocation();
  const { user, logoutMutation } = useAuth();
  const permissions = usePermissions();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useTranslation(['common', 'nav', 'plans']);

  // Získame systémové nastavenia pre farby
  const { getColorValue } = useSystemSettings();
  const primaryColor = getColorValue("theme.primaryColor", "#0099ff");
  
  const { data: remindersData } = useQuery({
    queryKey: ["/api/reminders"],
    enabled: !!user,
    queryFn: async () => {
      const response = await fetch('/api/reminders', {
        credentials: 'include',
        headers: { 'Accept': 'application/json' },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return Array.isArray(data.reminders) ? data.reminders : (Array.isArray(data) ? data : []);
    },
    staleTime: 60000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
  const reminders = Array.isArray(remindersData) ? remindersData : [];
  const reminderBadgeCount = reminders.filter((reminder: any) => {
    if (!reminder?.dueAt) return false;
    return new Date(reminder.dueAt).getTime() <= Date.now();
  }).length;

  // Billing data pre kontrolu plánu (fallback ak user.plan nie je nastavený)
  const { data: billingData } = useQuery<{ plan?: { name?: string } }>({
    queryKey: ['/api/billing'],
    enabled: !!user,
    staleTime: 1000 * 60 * 5,
  });
  const planName = (user?.plan || billingData?.plan?.name || '').toUpperCase();
  const hasTeamAccess = 
    user?.role === "admin" || user?.role === "superadmin" || user?.role === "owner" || user?.role === "beta_tester" ||
    planName === "BUSINESS" || planName === "FREELANCER" || planName === "PRO" ||
    (billingData?.plan?.name && /business|freelancer|pro/i.test(billingData.plan.name));
  
  // Definujeme všetky možné routes s farebnými ikonkami
  const allRoutes = [
    {
      label: t('nav:dashboard'),
      icon: LayoutDashboard,
      href: "/",
      active: isActiveUrl(location, "/"),
      requiresPermission: 'dashboard',
      color: primaryColor,
    },
    {
      label: t('nav:clients'),
      icon: Users,
      href: "/clients",
      active: isActiveUrl(location, "/clients"),
      requiresPermission: 'clients',
      color: "#10b981", // emerald-500
    },
    {
      label: t('nav:tasks'),
      icon: CheckSquare,
      href: "/tasks",
      active: isActiveUrl(location, "/tasks"),
      requiresPermission: 'tasks',
      color: "#f59e0b", // amber-500
    },
    {
      label: t('nav:invoices'),
      icon: FileText,
      href: "/invoices",
      active: isActiveUrl(location, "/invoices"),
      requiresPermission: 'invoices',
      color: "#2563eb", // Používame farbu faktúr
    },
    {
      label: t('nav:quotes'),
      icon: Calculator,
      href: "/price-quotes",
      active: isActiveUrl(location, "/price-quotes"),
      requiresPermission: 'price_quotes',
      color: "#8b5cf6", // violet-500
    },
    {
      label: t('nav:creditNotes'),
      icon: FileCheck,
      href: "/dobropisy",
      active: isActiveUrl(location, "/dobropisy"),
      requiresPermission: 'dobropisy',
      color: "#06b6d4", // cyan-500
    },
    {
      label: t('nav:reminders'),
      icon: Bell,
      href: "/reminders",
      active: isActiveUrl(location, "/reminders"),
      requiresPermission: null, // No permission required for reminders
      color: "#f97316", // orange-500
    },
    ...(hasTeamAccess
      ? [
          {
            label: t('nav:team'),
            icon: UserCog,
            href: "/team",
            active: isActiveUrl(location, "/team"),
            requiresPermission: null, // Admini, beta testeri a plány Business/Freelancer majú prístup
            color: "#ec4899", // pink-500
          },
        ]
      : []),
    {
      label: t('nav:profile'),
      icon: Settings,
      href: "/profile",
      active: isActiveUrl(location, "/profile"),
      requiresPermission: 'profile',
      color: "#64748b", // slate-500
    },
    {
      label: t('nav:subscription'),
      icon: CreditCard,
      href: "/subscribe",
      active: isActiveUrl(location, "/subscribe"),
      requiresPermission: null, // Všetci používatelia môžu spravovať svoje predplatné
      color: "#059669", // emerald-600
    },
    ...(user?.role === "admin" || user?.role === "superadmin" || user?.role === "owner"
      ? [
          {
            label: t('nav:admin'),
            icon: Shield,
            href: "/admin",
            active: isActiveUrl(location, "/admin"),
            requiresPermission: null, // Admini majú automaticky prístup
            color: "#dc2626", // red-600
          },
          {
            label: t('nav:subscriptionPlans'),
            icon: CreditCard,
            href: "/admin/subscription-plans",
            active: isActiveUrl(location, "/admin/subscription-plans"),
            requiresPermission: null, // Admini majú automaticky prístup
            color: "#7c3aed", // violet-600
          },
        ]
      : []),
  ];

  // Filtrovanie routes podľa oprávnení
  const routes = allRoutes.filter(route => {
    // Ak route nevyžaduje oprávnenie, vždy ho zobrazíme
    if (!route.requiresPermission) return true;
    
    // Pre adminov a superadminov zobrazíme všetko
    if (user?.role === 'admin' || user?.role === 'superadmin') return true;
    
    // Pre zamestnancov kontrolujeme oprávnenia
    if (user?.role === 'user') {
      return permissions.hasPermission(route.requiresPermission, 'view');
    }
    
    return true; // Pre iné prípady zobrazíme
  });

  // Mobile menu header – responzívna výška a safe area
  if (isMobile) {
    return (
      <div className={cn("w-full bg-card/95 backdrop-blur-sm border-b border-border sticky top-0 z-40", className)} {...props}>
        <div className="flex items-center justify-between px-3 sm:px-4 py-3 min-h-[56px]">
          <div className="flex items-center justify-between w-full min-w-0">
            <div className="flex-1 min-w-0 mr-2">
              {user?.companyLogo ? (
                <img 
                  src={user.companyLogo} 
                  alt={user.companyName || "Company Logo"}
                  className="h-7 sm:h-8 object-contain max-h-8 max-w-[180px] sm:max-w-[220px]" 
                />
              ) : (
                <div className="text-base sm:text-lg font-semibold tracking-tight truncate">
                  {user?.companyName || "TaskkroW"}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button 
                variant="ghost"
                size="icon"
                className="min-h-[44px] min-w-[44px] h-11 w-11 touch-manipulation"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>
        {mobileMenuOpen && typeof document !== 'undefined' && createPortal(
          <>
            {/* Mobile menu backdrop – cez portal nad všetkým */}
            <div 
              className="fixed inset-0 bg-black/50 z-[90]"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
            />
            
            {/* Mobile menu drawer – cez portal, nad bottom bar a FAB */}
            <div 
              className="fixed left-0 right-0 bottom-0 bg-background z-[95] overflow-y-auto overflow-x-hidden overscroll-contain pb-[env(safe-area-inset-bottom)] shadow-xl"
              style={{ top: 'calc(56px + env(safe-area-inset-top, 0px))' }}
            >
              <div className="px-4 py-6">
                <div className="space-y-2">
                  {routes.map((route) => (
                    <Link key={route.href} href={getLocalizedUrl(route.href)}>
                      <Button
                        variant={route.active ? "default" : "ghost"}
                        className={cn(
                          "w-full justify-start transition-all duration-200 rounded-lg",
                          "hover:bg-accent/80 hover:text-accent-foreground hover:translate-x-1",
                          route.active && "bg-gradient-to-r from-primary/15 to-secondary/15 text-primary shadow-md border border-primary/20"
                        )}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <route.icon 
                          className="mr-2 h-4 w-4 transition-colors duration-200"
                          style={{
                            color: route.active ? route.color : undefined
                          }}
                        />
                        <span className="flex items-center gap-2">
                          {route.label}
                          {route.href === "/reminders" && reminderBadgeCount > 0 && (
                            <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-semibold text-white">
                              {reminderBadgeCount}
                            </span>
                          )}
                        </span>
                      </Button>
                    </Link>
                  ))}
                  
                  <div className="mt-6">
                    <PlanSummary />
                  </div>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
      </div>
    );
  }

  // Komponent pre zobrazenie súhrnu plánu
  function PlanSummary() {
    const { user } = useAuth();
    const { data: tasks } = useQuery<Task[]>({
      queryKey: ["/api/tasks"],
    });
    const { data: billingData } = useQuery<{
      subscription?: { status: string; trialEnd?: string | null } | null;
    }>({
      queryKey: ['/api/billing'],
      enabled: !!user,
      staleTime: 1000 * 60 * 5,
    });
    const [, navigate] = useLocation();
    const { getColorValue } = useSystemSettings();
    const { t } = useTranslation(['common', 'nav', 'plans']);
    
    // Zobrazuje sa len pre adminov a superadminov (hlavných užívateľov), nie pre členov tímu
    if (user?.role === 'user') {
      return null;
    }
    
    // Získame primárnu farbu zo systémových nastavení
    const primaryColor = getColorValue("theme.primaryColor", "#0099ff");
    
    // Vytvárame variant farieb pre jednotlivé plány
    const generateColorVariant = (baseColor: string, opacity: number, isDark: boolean) => {
      if (isDark) {
        return `${baseColor}${Math.round(opacity * 100)}`;
      }
      return `${baseColor}${Math.round(opacity * 100)}`;
    };

    const taskCount = tasks?.length || 0;
    const trialDaysLeft = (() => {
      const sub = billingData?.subscription;
      if (sub?.status === 'trial' && sub?.trialEnd) {
        const trialEnd = new Date(sub.trialEnd);
        const now = new Date();
        const days = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        return days > 0 ? days : null;
      }
      return null;
    })();
    let planLimit = 5; // Predvolené limity pre plány
    let progress = 0;
    
    // Nastavenie limitov podľa plánu
    if (user?.plan === "FREELANCER") {
      planLimit = 20;
    } else if (user?.plan === "BUSINESS") {
      planLimit = Infinity;
    } else {
      planLimit = 3; // Free plan
    }
    
    progress = Math.min(100, (taskCount / (planLimit === Infinity ? 100 : planLimit)) * 100);
    const isExpired = user?.planExpiresAt ? new Date(user.planExpiresAt) < new Date() : false;
    const isPlanFull = taskCount >= planLimit;
    
    // Štýly s použitím dynamickej primárnej farby
    const getPlanBadgeClass = () => {
      const isDarkMode = document.documentElement.classList.contains('dark');
      
      switch (user?.plan) {
        case "FREELANCER":
          return `bg-primary/10 text-primary-600 dark:bg-primary/20 dark:text-primary-300`;
        case "BUSINESS":
          return `bg-primary/20 text-primary-700 dark:bg-primary/30 dark:text-primary-200`;
        default:
          return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
      }
    };
    
    const getProgressColor = () => {
      if (isPlanFull) return "bg-orange-200 dark:bg-orange-900/30";
      if (progress > 75) return "bg-orange-100 dark:bg-orange-900/20";
      if (user?.plan === "FREELANCER" || user?.plan === "BUSINESS") return `bg-primary/10 dark:bg-primary/20`;
      return "";
    };
    
    const getCardBgClass = () => {
      if (isPlanFull) return "bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-800";
      if (isExpired) return "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800";
      if (user?.plan === "FREELANCER" || user?.plan === "BUSINESS") {
        return `bg-primary/5 dark:bg-primary/10 border-primary/20 dark:border-primary/30`;
      }
      return "bg-background";
    };
    
    return (
      <Card className={`border-dashed mt-2 mb-4 mx-3 ${getCardBgClass()}`}>
        <CardContent className="p-3">
          <div className="flex justify-between items-center mb-3">
            <div className="font-medium flex items-center text-foreground flex-wrap gap-1">
              <span className={`inline-flex items-center rounded-md border px-2.5 py-0.5 mr-1.5 text-xs font-semibold ${getPlanBadgeClass()}`}>
                {t(`plans:${(user?.plan || "FREE").toLowerCase()}`)}
              </span>
              {trialDaysLeft !== null && (
                <span className="inline-flex items-center rounded-md bg-blue-600 text-white px-2 py-0.5 text-xs font-semibold">
                  {trialDaysLeft} {trialDaysLeft === 1 ? 'deň' : trialDaysLeft < 5 ? 'dni' : 'dní'}
                </span>
              )}
              <span className="text-foreground font-medium">
                {t('plans:currentPlan').split(' ')[0]}
              </span>
            </div>
            {isExpired && <span className="text-xs text-destructive font-medium">{t('plans:expired')}</span>}
          </div>
          
          <div className="mb-3">
            <div className="flex justify-between text-xs mb-1 text-foreground">
              <span>{t('plans:tasks')}: {taskCount} {planLimit !== Infinity ? `${t('common:of')} ${planLimit}` : ''}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress 
              value={progress} 
              className={`h-2 ${getProgressColor()}`} 
            />
          </div>
          
          <Button 
            size="sm" 
            className="w-full" 
            variant={isPlanFull || progress > 75 ? "default" : "secondary"}
            onClick={() => navigate(getLocalizedUrl("/subscribe"))}
          >
            <CreditCard className="h-3.5 w-3.5 mr-1.5" />
            {isPlanFull ? t('plans:upgradePlanNow') : (user?.plan === "BUSINESS" ? t('plans:managePlan') : t('plans:upgradePlan'))}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Desktop sidebar with collapsible functionality
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  return (
    <>
      <SidebarHeader>
        <div className="px-2 py-2 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              {user?.companyLogo && !isCollapsed ? (
                <img 
                  src={user.companyLogo} 
                  alt={user.companyName || "Company Logo"}
                  className="h-8 object-contain w-full" 
                />
              ) : (
                <div className={cn(
                  "text-lg font-semibold tracking-tight",
                  isCollapsed && "sr-only"
                )}>
                  {user?.companyName || "TaskkroW"}
                </div>
              )}
              {isCollapsed && user?.companyLogo && (
                <img 
                  src={user.companyLogo} 
                  alt={user.companyName || "Company Logo"}
                  className="h-8 w-8 object-contain mx-auto" 
                />
              )}
            </div>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className={isCollapsed ? "sr-only" : ""}>
            MENU
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {routes.map((route) => (
                <SidebarMenuItem key={route.href}>
                  <SidebarMenuButton 
                    asChild 
                    isActive={route.active}
                    tooltip={isCollapsed ? route.label : undefined}
                    className={cn(
                      "group transition-all duration-200 rounded-lg relative overflow-hidden",
                      "hover:bg-accent/80 hover:text-accent-foreground hover:translate-x-1",
                      route.active && "bg-gradient-to-r from-primary/10 to-secondary/10 text-primary border-l-4 border-primary shadow-sm"
                    )}
                  >
                    <Link href={getLocalizedUrl(route.href)}>
                      <route.icon 
                        className={cn(
                          "h-4 w-4 transition-colors duration-200",
                          route.active ? "text-current" : "text-muted-foreground group-hover:text-current"
                        )}
                        style={{
                          color: route.active ? route.color : undefined
                        }}
                      />
                      <span className={cn("flex items-center gap-2", isCollapsed ? "sr-only" : "")}>
                        {route.label}
                        {route.href === "/reminders" && reminderBadgeCount > 0 && (
                          <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-semibold text-white">
                            {reminderBadgeCount}
                          </span>
                        )}
                      </span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        
        {/* Plán presunutý hore pod menu */}
        {!isCollapsed && <PlanSummary />}
      </SidebarContent>

      <SidebarFooter>
      </SidebarFooter>
    </>
  );
}